import React, { useLayoutEffect, useRef, useState } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import Ellipses from "components/shared/loading/Ellipses";
import spring, { toString } from "css-spring";
import styled, { keyframes } from "styled-components";

// Stream states
export const DEFAULT_STATE = "default";
export const LOADING_STATE = "loading";
export const STREAMING_STATE = "streaming";

// Variants
const SERVER_VARIANT = "server";
const USER_VARIANT = "user";

export const VARIANTS = [SERVER_VARIANT, USER_VARIANT];
export const STATES = [DEFAULT_STATE, LOADING_STATE, STREAMING_STATE];
export const ChatBubblePropTypes = {
  children: PropTypes.node,
  cursorAsSpan: PropTypes.bool,
  slideUp: PropTypes.bool,
  streamState: PropTypes.oneOf(STATES),
  variant: PropTypes.oneOf(VARIANTS)
};

export const userBubbleConfig = (attrs) => {
  return {
    children: null,
    slideUp: true,
    streamState: DEFAULT_STATE,
    variant: USER_VARIANT,
    ...attrs
  };
};

export const serverBubbleConfig = (attrs) => {
  return {
    children: null,
    slideUp: true,
    streamState: DEFAULT_STATE,
    variant: SERVER_VARIANT,
    ...attrs
  };
};

const ChatBubble = (props) => {
  const { children, cursorAsSpan, slideUp, streamState, variant } = props;
  const [travelDistance, setTravelDistance] = useState(0);
  const bubbleRef = useRef();

  // Calculate the slide-in distance based on position of the chat bubble
  // in relation to the chat area bottom.
  useLayoutEffect(() => {
    const { parentElement } = bubbleRef.current;
    const currentElement = bubbleRef.current;
    const parentHeight = parentElement.scrollHeight;
    const elementBottom =
      currentElement.offsetTop + currentElement.offsetHeight;

    const distanceToTravel =
      elementBottom < parentHeight
        ? parentHeight - elementBottom
        : parentHeight;
    setTravelDistance(distanceToTravel);
  }, [bubbleRef]);

  // Setup slide-in animation
  const animationKeyframes = slideUp
    ? toString(
        spring(
          { bottom: `-${travelDistance}px` },
          { bottom: "0" },
          { damping: 20, stiffness: 80 }
        )
      )
    : "none";

  const AnimatedDiv = styled.div`
    animation: ${keyframes`${animationKeyframes}`} 0.6s linear;
    position: relative;
  `;

  const sanitizedChildren =
    typeof children === "string"
      ? DOMPurify.sanitize(children, {
          ALLOWED_ATTR: [],
          ALLOWED_TAGS: [
            "b",
            "i",
            "em",
            "strong",
            "code",
            "pre",
            "blockquote",
            "ul",
            "ol",
            "li",
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "br",
            "p"
          ]
        })
      : children;

  return (
    <AnimatedDiv
      ref={bubbleRef}
      className={classnames(
        "chat-bubble--container",
        `chat-bubble--variant_${variant}`
      )}
    >
      <div
        className={`chat-bubble--content chat-bubble--content_${streamState}`}
      >
        {streamState === LOADING_STATE && <Ellipses />}
        {streamState !== LOADING_STATE && (
          <>
            {typeof children === "string" && variant === SERVER_VARIANT ? (
              <span dangerouslySetInnerHTML={{ __html: sanitizedChildren }} />
            ) : (
              children
            )}
            {streamState === STREAMING_STATE && cursorAsSpan && (
              <span className="typewriter--cursor" />
            )}
          </>
        )}
      </div>
    </AnimatedDiv>
  );
};

ChatBubble.propTypes = ChatBubblePropTypes;

ChatBubble.defaultProps = {
  children: null,
  cursorAsSpan: false,
  slideUp: false,
  streamState: DEFAULT_STATE,
  variant: SERVER_VARIANT
};

export default React.memo(ChatBubble);
